import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { baseCell, booleanCell, currencyCell, dateCell } from 'components/cells';
import productStatusCell from 'components/cells/product-status-cell';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators,
  numberFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import dayjs from 'dayjs';
import { IMovement, Movement } from 'features/inventory/movements/types';
import { ProductWarehouseStatus } from 'features/inventory/products/constants';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { StatusOption } from 'shared/types/general';

export interface IMovementDetailParams {
  movement__id: number;
  product__id: number;
  warehouse: number;
  type: number;
  created_by: number;
  product_batch: string;
  purchase_order: number | null;
  timestamp: string;
  discounted: boolean;
}

export function UninvoicedMovementsColumns(
  types: string[],
  editableExpiration = false
): GridColDef[] {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const translationPrefix = 'entities.movement';

  const statusOptions: StatusOption[] = Object.entries(ProductWarehouseStatus).map(
    ([display_name, key]) => {
      return {
        value: Number(key),
        display_name: String(display_name)
      };
    }
  );

  const movementsDetail = (params: IMovementDetailParams) => {
    const searchParams = createSearchParams(
      Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }

        return acc;
      }, {})
    );

    navigate({
      pathname: '/tenant/movements/detail/',
      search: `?${searchParams.toString()}`
    });
  };

  const toMovementDetailParams = (movement: Movement): IMovementDetailParams => ({
    movement__id: movement.id,
    product__id: movement.product,
    warehouse: movement.warehouse,
    type: movement.type,
    created_by: movement.created_by,
    product_batch: movement.product_batch,
    purchase_order: movement.purchase_order,
    timestamp: movement.timestamp ? dayjs(movement.timestamp).format('YYYY-MM-DD') : undefined,
    discounted: movement.discounted
  });

  const columns: GridColDef[] | any[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        return [
          {
            label: 'Dettaglio',
            icon: <ManageSearchIcon />,
            onClick: () => movementsDetail(toMovementDetailParams(params.row)),
            visible: true
          }
        ];
      },
      filterable: false
    },
    {
      field: 'moved_quantity',
      headerName: t(`${translationPrefix}.moved_quantity`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      renderCell: (params: GridRenderCellParams<any>) => {
        const movement = params.row as IMovement;
        return (
          <>
            {movement.direction === 1 ? (
              <ArrowDownwardIcon color="success" />
            ) : (
              <ArrowUpward color="warning" />
            )}{' '}
            {movement.moved_quantity}
          </>
        );
      }
    },
    {
      field: 'product_code',
      headerName: t(`global.product`),
      filterOperators,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 150,
      type: 'singleSelect',
      valueOptions: statusOptions.map((opt) => {
        return {
          value: String(opt.value),
          label: t(`entities.product.status_options.${opt.value}`)
        };
      }),
      filterable: true,
      sortable: true,
      filterOperators: singleSelectFilterOperators,
      renderCell: productStatusCell(t)
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterOperators,
      sortable: true,
      width: 300,
      renderCell: baseCell
    },
    {
      field: 'timestamp',
      headerName: t(`${translationPrefix}.timestamp`),
      filterable: true,
      filterOperators: dateFilterOperators,
      sortable: true,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      valueFormatter: (params) => params.value && dayjs(params.value).format('DD/MM/YYYY'),
      width: 250,
      renderCell: dateCell
    },
    {
      field: 'discounted',
      headerName: t(`${translationPrefix}.discounted`),
      filterable: true,
      filterOperators: booleanFilterOperators,
      sortable: true,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'unit_price',
      headerName: t(`${translationPrefix}.unit_price`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      width: 150,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'price',
      headerName: t(`${translationPrefix}.price`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      width: 150,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'category',
      headerName: t(`entities.product.category`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'product_batch',
      headerName: t(`${translationPrefix}.product_batch`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'expiration_date',
      headerName: t(`${translationPrefix}.expiration_date`),
      dateFilterOperators,
      valueFormatter: (params) => dayjs(params.value).format('DD/MM/YYYY'),
      editable: editableExpiration,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      width: 250,
      renderCell: dateCell
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      width: 250,
      renderCell: baseCell,
      sortable: false,
      filterable: false
    },
    {
      field: 'product_notes',
      headerName: t(`global.product-notes`),
      width: 250,
      renderCell: baseCell,
      sortable: false,
      filterable: false
    },
    {
      field: 'type_code',
      headerName: t(`${translationPrefix}.type`),
      type: 'singleSelect',
      valueOptions: types,
      width: 250,
      filterOperators: singleSelectFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'order_code',
      headerName: t(`entities.movement.order_code`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'order_date',
      headerName: t(`${translationPrefix}.order_date`),
      width: 200,
      renderCell: dateCell
    },
    {
      field: 'transport_document',
      headerName: t(`${translationPrefix}.transport_document`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'transport_document_date',
      headerName: t(`${translationPrefix}.transport_document_date`),
      width: 200,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'billable',
      headerName: t(`entities.ddt.billable`),
      filterOperators: booleanFilterOperators,
      width: 150,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'credit_memo_ref',
      headerName: t(`entities.ddt.credit_memo_ref`),
      filterable: false,
      sortable: false,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'credit_memo_date',
      headerName: t(`entities.ddt.credit_memo_date`),
      filterable: false,
      sortable: false,
      width: 200,
      renderCell: dateCell
    },
    {
      field: 'credit_memo_amount',
      headerName: t(`entities.ddt.credit_memo_amount`),
      filterable: false,
      sortable: false,
      width: 200,
      renderCell: currencyCell
    },
    {
      field: 'author',
      headerName: t(`${translationPrefix}.created_by`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    }
  ];

  return columns;
}
